import { Injectable, inject } from '@angular/core';
import { ThemesData } from '@b2b/common/resolvers';
import { DeepPartial, TODO } from '@b2b/types/core/utils';
import { FeatureFlag } from '@b2b/types/core';
import { HelpersService } from '../helpers/helpers.service';

/**
 * ThemesHelperService (Angular 18)
 *
 * Replaces: apps/b2b-ui/common/services/themesHelper/themesHelper.service.js
 * Manages theme data, feature flags, text/email variables, and company configuration.
 */
@Injectable({ providedIn: 'root' })
export class ThemesHelperService {
  private helpers = inject(HelpersService);

  model: Record<string, unknown> = {};

  readonly textVariables: Record<string, string> = {
    'checkout-confirm-message': 'Thank you for your purchase, an email will be sent to you confirming your purchase.',
    'checkout-confirm-page-description':
      'Praesent et pretium quam, in tincidunt tortor. Integer dolor eros, interdum et mauris eget, viverra suscipit libero. In non dapibus leo.',
    'checkout-confirm-page-heading': 'Order confirmation',
    'checkout-confirm-return-link': '',
    'checkout-confirm-return-text': '',
    'checkout-process-coupon-code-heading': 'Coupons',
    'checkout-process-coupon-code-message': 'Have a coupon code?',
    'checkout-process-coupon-code-placeholder': 'Promo code',
    'checkout-process-page-description':
      'Proin pharetra nisi eu metus semper, id pretium nisi tempus. Praesent vitae enim vel libero facilisis tristique at in velit. Fusce quam leo, ullamcorper at varius ut, pretium ac quam.',
    'checkout-process-page-heading': 'Your order',
    'checkout-process-page-terms-MARKUP': '<p>HTML Allowed</p>',
    'checkout-process-promo-code-message': 'Have a gift card?',
    'checkout-process-promo-code-placeholder': 'Gift card number',
    'checkout-process-recaptcha-message': 'Your order cannot be completed at this time. Please call us for assistance.',
    'donation-button-submit': 'Continue',
    'donation-description': '',
    'donation-page-description': '',
    'donation-page-footer': '',
    'donation-page-heading': '',
    'donation-recognition': 'Anonymous',
    'donation-subheading': 'We appreciate any amount you can give!',
    'event-discounted-price-title': 'discount price (WL)',
    'event-full-price-title': 'Full price (WL)',
    'event-limit-exceeded-title': 'Limit exceeded (WL)',
    'event-retail-price-title': 'Full price (WL)',
    'events-button-combos': 'BUY COMBO',
    'events-button-tickets': 'TICKETS',
    'events-list-page-membership-caption': 'Event prices will reflect applicable membership benefits. (WL)',
    'events-list-page-membership-heading': 'Thank you for being a member! (WL)',
    'events-list-page-membership-join-caption': 'Events list page membership join caption (WL)',
    'events-list-page-membership-join-heading': 'Events list page membership join heading (WL)',
    'events-page-combo-deals-heading': 'Private events instead of Combos',
    'events-page-description':
      'Nam facilisis nulla ipsum, aliquet placerat lectus vehicula a. Maecenas ac est sagittis, semper mauris sit amet, ultrices nunc. Quisque neque arcu, laoreet accumsan lacus quis, vestibulum condimentum libero. Cras congue lectus nec enim consequat, ut tempus sem vulputate.',
    'events-page-heading': 'Events',
    'google-analytics-tracking-code-test': 'UA-XXXXXXX-X',
    'google-analytics-tracking-code': 'UA-XXXXXXX-X',
    'gtm-tracking-code-test': 'GTM-XXXXXXX',
    'gtm-tracking-code': 'GTM-XXXXXXX',
    'header-hide-login': 'false',
    'membership-aux-groups-title': 'Auxiliary Groups',
    'membership-join-aux-desc': 'You must have a membership in order to purchase an Auxiliary group membership',
    'membership-join-page-description':
      'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Morbi pretium lorem nec elementum tempus. Cras sollicitudin pellentesque leo nec malesuada. Morbi vel dolor urna. Curabitur congue interdum porttitor. Ut molestie lectus vel sollicitudin ultricies. Sed congue consectetur augue vel volutpat. Phasellus sodales nulla vel leo lacinia pellentesque. Nunc faucibus, risus sit amet eleifend facilisis, enim risus scelerisque ex, eget hendrerit mauris est at neque. Sed quis vehicula odio. Integer egestas, dui sed ultricies laoreet, orci augue tristique odio, vitae sollicitudin erat est ac quam. Mauris dictum vitae velit fermentum tincidunt.(WL)',
    'membership-join-page-heading': 'MEMBERSHIPS (WL)',
    'membership-join-tab-title-aux': 'Auxiliary Groups',
    'membership-join-tab-title-main': 'Memberships',
    'membership-profile-banner-caption': 'Call us for more information',
    'membership-profile-banner-heading': 'Membership Information',
    'membership-profile-banner-upgrade-caption': 'Your current membership payment will apply towards the total (WL).',
    'membership-profile-banner-upgrade-disallowed-caption':
      'We are unable to process your upgrade online at this time. (WL).',
    'membership-profile-banner-upgrade-disallowed-heading': 'Please contact us to upgrade your membership (WL).',
    'membership-profile-banner-upgrade-heading': 'Upgrade today for additional benefits (WL).',
    'membership-profile-page-description':
      'Praesent et pretium quam, in tincidunt tortor. Integer dolor eros, interdum et mauris eget, viverra suscipit libero. In non dapibus leo. (WL)',
    'membership-profile-page-heading': 'MY MEMBERSHIP (WL)',
    'membership-renew-detail-page-description': 'Renew Detail (WL)',
    'membership-renew-detail-page-heading': 'Renew Detail Membership (WL)',
    'membership-renew-error-msg': 'You must have a valid membership to purchase.',
    'membership-renew-page-description': ' (WL)',
    'membership-renew-page-heading': 'Renew Membership (WL)',
    'membership-upgrade-page-description': ' (WL)',
    'membership-upgrade-page-heading': 'Renew Membership (WL)',
    'membership-verification-email-only': 'false',
    'orders-calendar-page-description-MARKUP': 'We are open 365 days a year. Select a date for your purchase.',
    'orders-calendar-page-heading': 'When would you like to visit?',
    'orders-combo-page-description': 'Choose which events you would like to add to your combo',
    'orders-combo-page-heading': 'Add events to your combo',
    'orders-forms-page-description': ' ',
    'orders-forms-page-heading': 'Forms',
    'orders-page-description':
      'Quisque at euismod justo, quis eleifend tellus. Quisque venenatis molestie neque sit amet hendrerit. Proin accumsan tempor pulvinar.',
    'orders-page-heading': '',
    'orders-tickets-page-upsells-caption': 'Other events you may be interested in:',
    'orders-tickets-page-upsells-heading': 'Also Happening',
    'privacy-policy-description': 'Some HTML or regular text.',
  };

  // ─── Initialization ───────────────────────────────────────────────────────

  init(theme?: ThemesData): void {
    if (theme) {
      this.setModel(theme);
    } else {
      this.resetModel();
    }
  }

  // ─── Model management ────────────────────────────────────────────────────

  setModel(model: ThemesData | Record<string, unknown>): void {
    this.model = model as Record<string, unknown>;
  }

  getModel(): Record<string, unknown> {
    return this.model;
  }

  checkExists(): boolean {
    const company = (this.model as any)?.company;
    return !!(company && !this.helpers.isEmptyObject(company));
  }

  resetModel(): void {
    this.setModel({
      links: [],
      company: {},
      textVariables: {},
      emailVariables: {},
    });
  }

  // ─── Links & Logo ────────────────────────────────────────────────────────

  getLinks(): TODO[] {
    const links = (this.model as any)?.links;
    if (!links) return [];

    const result: TODO[] = [];

    for (const link of links) {
      if (link.key !== 'logo') {
        result.push(link);
      } else {
        (this.model as any).logo = link;
      }
    }

    return result;
  }

  getLogo(): TODO {
    const links = (this.model as any)?.links;
    if (!(this.model as any).logo && links) {
      for (const link of links) {
        if (link.key === 'logo') {
          (this.model as any).logo = link;
          break;
        }
      }
    }
    return (this.model as any).logo;
  }

  // ─── Company & Feature Flags ──────────────────────────────────────────────

  getCompany(key?: string): TODO {
    if (!Object.prototype.hasOwnProperty.call(this.model, 'company')) {
      return '';
    }

    const company = (this.model as any).company;

    if (!key) {
      return company;
    }

    return Object.prototype.hasOwnProperty.call(company, key) ? company[key] : false;
  }

  getFeatureFlag(flag: FeatureFlag): boolean {
    const featureFlags = [
      'enableSalesView',
      'enableSalesTax',
      'enable3DS',
      'enableCORSCheckout',
      'enableCustomerMerge',
      'enableMembershipEmailValidation',
      'enableAppsIntegration',
      'enableEnhancedTemplatePublish',
    ];

    return featureFlags.includes(flag) ? this.getCompany(flag) === true : false;
  }

  // ─── Configuration & Variables ───────────────────────────────────────────

  getFeaturesConfig(feature: string, key?: string): TODO {
    const config = (this.model as any)?.featuresConfig;
    if (!config) return '';

    if (!key) return config;

    return config[feature]?.[key] ?? false;
  }

  getCCValidationSettings(key?: string): TODO {
    const settings = (this.model as any)?.creditCardValidationSettings || {};
    if (!key) return settings;
    return settings[key] ?? false;
  }

  getTextVariable(key: string, value?: TODO): TODO {
    let result = value || '';

    if (this.textVariables.hasOwnProperty(key)) {
      result = this.textVariables[key];
    }

    if ((this.model as any)?.textVariables?.hasOwnProperty(key)) {
      result = (this.model as any).textVariables[key];
    }

    return result;
  }

  getEmailVariable(key: string, value?: TODO): TODO {
    const result = value || '';
    return (this.model as any)?.emailVariables?.[key] ?? result;
  }

  getHTMLVariable(key: string): string {
    return (this.model as any)?.textVariables?.[key + '-MARKUP'] ?? '';
  }

  getMembershipConfig(obj: string, key: string): TODO {
    return (this.model as any)?.membershipConfig?.[obj]?.[key] ?? '';
  }
}
